import { ExternalLinkIcon } from "@chakra-ui/icons";
import { Box, Collapse, Flex, Link, Text } from "@chakra-ui/react";
import { isEqual } from "lodash";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { PhysicalQuantity } from "@nens/rana/api-v1";
import { AsyncSubmitFunction, RanaForm } from "@nens/ranalib";
import {
  capitalizeFirst,
  displayBytes,
  objectValues,
  removeNullUndefined,
} from "@nens/shared-utils";

import { DetailTable } from "@/components/detail-table";
import { HeaderActionSection } from "@/components/header-action-section";
import { useProjectActionAllowed } from "@/hooks/use-action-allowed-in-project";
import { Profile } from "@/lib/profile";
import { FileDataTypeEnum, RanaFileIcon, getFileDisplayType } from "@/lib/rana-file-data-type";
import {
  useFileDescriptorQuery,
  useFileDescriptorViewQuery,
} from "@/queries/query-file-descriptors";
import { useRasterDefaultUnitsQuery } from "@/queries/query-raster-default-units";
import { useColormapTypeQuery } from "@/queries/query-wms-legend";
import { ProjectFile } from "@/types/api-model-simple-names";
import { fromISODurationToString } from "@/utils/from-iso-duration-to-string";
import { getCompleteName } from "@/utils/get-complete-name";
import { getPrettyTime } from "@/utils/get-pretty-time";
import { isTypedDescriptor } from "@/utils/is-descriptor-type";

import { useSetFileDescription } from "../api/set-file-description";
import { useUpdateRasterMeta } from "../api/update-raster-meta";

import { RasterUnitPicker } from "./project-file-raster-unit-picker";

interface ProjectFileMetadataProps {
  file: ProjectFile;
  fileRef: string | undefined;
}

type DetailFormValues = {
  description: string;
  rasterStyle: PhysicalQuantity | null;
};

export function ProjectFileMetadata(props: ProjectFileMetadataProps) {
  return <ProjectFileMetadataInner {...props} />;
}

function ProjectFileMetadataInner(props: ProjectFileMetadataProps) {
  const { file, fileRef } = props;
  const intl = useIntl();
  const { canWriteProject } = useProjectActionAllowed();

  const { mutateAsync: updateFileDescription, isMutating: isMutatingDescription } =
    useSetFileDescription(file.descriptorId);

  const { data: descriptor } = useFileDescriptorQuery(file.descriptorId);
  const fileDescription = descriptor?.description;

  const { mutateAsync: updateRasterMeta, isMutating: isMutatingRasterMeta } =
    useUpdateRasterMeta(descriptor);

  const isRaster = isTypedDescriptor(descriptor, "raster");
  const rasterDescriptor = isRaster ? descriptor : undefined;

  const userSuppliedMeta = rasterDescriptor?.userSuppliedMeta;
  const metaRangeType = rasterDescriptor?.meta.rangeType;

  const fileRasterStyle = rasterDescriptor
    ? userSuppliedMeta?.physicalQuantity ?? metaRangeType?.physicalQuantity ?? "unknown"
    : null;

  const defaultRasterUnit = userSuppliedMeta?.unit ?? metaRangeType?.unit;

  const [_rasterUnit, setRasterUnit] = useState(defaultRasterUnit);
  const rasterUnit = _rasterUnit ?? defaultRasterUnit;

  const { data: defaultUnits, isSuccess: isDefaultUnitsSuccess } = useRasterDefaultUnitsQuery(
    rasterDescriptor?.id
  );

  const { data: raster, isLoading: isRasterLoading } = useFileDescriptorViewQuery(
    rasterDescriptor?.id,
    "lizard-raster"
  );

  const { data: colormapType, isLoading: isColormapTypeLoading } = useColormapTypeQuery(
    rasterDescriptor?.id,
    raster?.wms_info.layer,
    raster?.options.styles
  );

  const rasterIsGradient = colormapType === "gradient";

  const showRasterUnitPicker =
    isRaster &&
    isDefaultUnitsSuccess &&
    !isRasterLoading &&
    !isColormapTypeLoading &&
    rasterIsGradient;

  // FUTURE: From here should probably be placed in a separate file
  const getFileDetails = (editing: boolean) =>
    removeNullUndefined([
      {
        name: intl.formatMessage({
          id: "file.version",
          defaultMessage: "Version",
        }),
        value:
          fileRef ??
          intl.formatMessage({
            id: "file.latest_version",
            defaultMessage: "Latest",
          }),
      },
      {
        name: intl.formatMessage({ id: "file.added_by", defaultMessage: "Added by" }),
        value: (
          <Flex alignItems="center">
            <Profile inTable user={file.user} />
            <Text ml={2}>{getCompleteName(file.user)}</Text>
          </Flex>
        ),
      },
      file.descriptor?.dataType !== FileDataTypeEnum.Schematisation
        ? {
            name: intl.formatMessage({ id: "file.size", defaultMessage: "Size" }),
            value: displayBytes(file.size),
          }
        : null,
      {
        name: intl.formatMessage({ id: "file.data_type", defaultMessage: "Data type" }),
        value: (
          <Box>
            <RanaFileIcon fileOrDirectory={file} boxSize={4} />
            {getFileDisplayType(file)}
          </Box>
        ),
      },
      {
        name: intl.formatMessage({ id: "file.date_modified", defaultMessage: "Date modified" }),
        value: getPrettyTime(file.lastModified),
      },
      {
        name: intl.formatMessage({
          id: "file.description",
          defaultMessage: "Description",
        }),
        value: (
          <>
            <Collapse unmountOnExit in={editing}>
              <RanaForm.Textarea
                field="description"
                placeholder={intl.formatMessage({
                  id: "file.add_description",
                  defaultMessage: "Add a description",
                })}
                size="sm"
                m={0}
                isDisabled={isMutatingDescription}
              />
            </Collapse>

            <Collapse in={!editing}>{fileDescription || "-"}</Collapse>
          </>
        ),
      },
    ]);

  const getRasterDetails = (editing: boolean) => [
    ...(isRaster
      ? [
          {
            name: intl.formatMessage({ id: "file.raster.style", defaultMessage: "Style" }),
            value: (
              <RanaForm.Dropdown
                m={0}
                field="rasterStyle"
                w="full"
                isReadOnly={isMutatingRasterMeta || !editing}
                options={objectValues(PhysicalQuantity).map((style) => ({
                  label: capitalizeFirst(style.replace(/_/g, " ")),
                  value: style,
                }))}
              />
            ),
          },
        ]
      : []),

    ...(showRasterUnitPicker
      ? [
          {
            name: intl.formatMessage({
              id: "file.raster.unit",
              defaultMessage: "Unit",
            }),
            value: (
              <RasterUnitPicker
                rasterUnit={rasterUnit}
                setRasterUnit={editing && !isMutatingRasterMeta ? setRasterUnit : undefined}
                defaultUnits={defaultUnits}
              />
            ),
          },
        ]
      : []),
  ];

  const scenarioMeta =
    descriptor && isTypedDescriptor(descriptor, "scenario") ? descriptor.meta : null;

  const scenarioDetails = scenarioMeta
    ? [
        {
          name: intl.formatMessage({
            id: "file.scenario.simulation_name",
            defaultMessage: "Simulation name",
          }),
          value: scenarioMeta.simulation.name,
        },
        {
          name: intl.formatMessage({
            id: "file.scenario.schematisation_name",
            defaultMessage: "Model schematisation name",
          }),
          value: scenarioMeta.schematisation.name,
        },
        {
          name: intl.formatMessage({
            id: "file.scenario.model_software",
            defaultMessage: "Model software",
          }),
          value: scenarioMeta.simulation.software?.id,
        },
        {
          name: intl.formatMessage({
            id: "file.scenario.model_version",
            defaultMessage: "Model version",
          }),
          value: scenarioMeta.simulation.software?.version,
        },
        {
          name: intl.formatMessage({
            id: "file.scenario.simulation_run_date",
            defaultMessage: "Simulation run date",
          }),
          value:
            scenarioMeta.simulation.createdAt && getPrettyTime(scenarioMeta.simulation.createdAt),
        },
        {
          name: intl.formatMessage({
            id: "file.scenario.duration",
            defaultMessage: "Duration",
          }),
          value:
            scenarioMeta.simulation.duration &&
            fromISODurationToString(intl, scenarioMeta.simulation.duration),
        },
        {
          name: intl.formatMessage({
            id: "file.scenario.start_end",
            defaultMessage: "Start / end",
          }),
          value:
            scenarioMeta.simulation.interval &&
            scenarioMeta.simulation.interval
              .map((value) => getPrettyTime(new Date(value)))
              .join(" - "),
        },
        {
          name: intl.formatMessage({
            id: "file.external_link",
            defaultMessage: "External link",
          }),
          value: scenarioMeta.managementUrl && (
            <Link href={scenarioMeta.managementUrl} isExternal>
              <FormattedMessage id="file.threedi_management_page" defaultMessage="Rana HCC page" />
              <ExternalLinkIcon ml={1} mb={1} />
            </Link>
          ),
        },
      ]
    : [];

  const getExtendedFileDetails = (editing: boolean) => [
    ...getFileDetails(editing),
    ...getRasterDetails(editing),
    ...scenarioDetails,
  ];

  const submit: AsyncSubmitFunction<DetailFormValues> = async ({ description, rasterStyle }) => {
    const rasterMetaUpdates: Parameters<typeof updateRasterMeta>[0] = {};

    if (rasterStyle && rasterStyle !== fileRasterStyle) {
      rasterMetaUpdates.physicalQuantity = rasterStyle;
    }

    if (!isEqual(rasterUnit, defaultRasterUnit)) {
      rasterMetaUpdates.unit = rasterUnit;
    }

    if (Object.keys(rasterMetaUpdates).length > 0) {
      await updateRasterMeta(rasterMetaUpdates);
    }

    if (description !== fileDescription) {
      await updateFileDescription({ id: file.id, description });
    }
  };

  return (
    <HeaderActionSection
      title="Metadata"
      action={{
        type: "edit",
        canEdit: canWriteProject,
        isMutating: isMutatingDescription || isMutatingRasterMeta,
        initialValues: {
          description: fileDescription ?? "",
          rasterStyle: fileRasterStyle,
        },
        onSubmit: submit,
      }}
    >
      {(editing) => <DetailTable rows={getExtendedFileDetails(editing)} />}
    </HeaderActionSection>
  );
}
